// src/pages/HomePage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Paper,
  keyframes,
  useTheme,
} from '@mui/material';
import {
  PersonAdd,
  People,
  Pets,
  CalendarMonth,
  Home,
  Event,
  RequestQuote,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import { colors } from '../theme/theme';
import { STICKY_MENU_MEDIA_QUERIES } from '../components/forms/shared/MediaQueryUtils';
import useLocalisation from '../hooks/useLocalisation';
import { useErrorHandler, ErrorDisplayType, ERROR_MESSAGES } from '../errors';
import { PageError } from '../components/common/PageError';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { bookingsAPI } from '../services/api';
import DateTimeDisplay from '../components/common/DateDisplay';
import VisuallyHiddenHeading from '../components/common/VisuallyHiddenHeading';
import { getCustomIcon } from '../utils/customIcons';

interface QuickAction {
  to: string;
  icon: React.ReactElement;
  titleKey: string;
  description: string;
}

interface Stat {
  number: string;
  labelKey: string;
  tab: 'check-ins' | 'check-outs' | 'active-bookings' | 'overdue';
}

interface DashboardStats {
  activeBookings: number;
  checkInsToday: number;
  checkOutsToday: number;
  availableKennels: number;
  overdueInvoices: number;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ActionCardsContainer = styled(Box)({
  animation: `${fadeIn} 0.6s ease-in-out`,
});

const ActionCardButton = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  border:
    theme.palette.mode === 'dark'
      ? `2px solid ${theme.palette.primary.main}`
      : `2px solid ${theme.palette.primary.dark}`,

  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.tertiary.light,

  '&:last-child': {
    paddingBottom: 0,
  },

  // Tablet (600px+)
  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    padding: '30px',
    flexDirection: 'column',
    flexGrow: 1,
  },

  // Desktop (900px+)
  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    padding: '35px',
    flexDirection: 'column',
  },

  // Large (1200px+)
  [STICKY_MENU_MEDIA_QUERIES.large]: {
    padding: '10px',
    flexDirection: 'column',
  },

  // MediumLarge (1400-1535px)
  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    padding: '10px',
    flexDirection: 'column',
  },

  // XLarge (1536px+)
  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    padding: '10px',
    flexDirection: 'column',
  },

  // XXLarge (2560px+)
  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    padding: '50px',
    flexDirection: 'column',
  },

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.tertiary.dark,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[100]
        : theme.palette.grey[0],
  },
}));

const ActionIcon = styled(Box)({
  marginBottom: 0,
  display: 'flex',
  justifyContent: 'center',

  '& .MuiSvgIcon-root': {
    fontSize: '2.5rem',
    margin: '0 0.5rem 0 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    marginBottom: '15px',
    '& .MuiSvgIcon-root': {
      fontSize: '3rem',
    },
  },

  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    marginBottom: '20px',
    '& .MuiSvgIcon-root': {
      fontSize: '3.5rem',
    },
  },

  [STICKY_MENU_MEDIA_QUERIES.large]: {
    marginBottom: '25px',
    '& .MuiSvgIcon-root': {
      fontSize: '4rem',
    },
  },

  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    marginBottom: '25px',
    '& .MuiSvgIcon-root': {
      fontSize: '4.25rem',
    },
  },

  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    marginBottom: '5px',
    '& .MuiSvgIcon-root': {
      fontSize: '4.5rem',
    },
  },

  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    marginBottom: '30px',
    '& .MuiSvgIcon-root': {
      fontSize: '5rem',
    },
  },
});

const ActionTitle = styled(Typography)({
  fontSize: '2rem',
  fontWeight: 800,
  marginBottom: 0,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  transition: 'transform 0.3s ease',
  whiteSpace: 'nowrap',

  '@media (min-width: 600px)': {
    fontSize: '2.4rem',
  },

  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    marginBottom: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    marginBottom: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.large]: {
    marginBottom: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    marginBottom: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    marginBottom: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    marginBottom: '10px',
  },
});

const StatsSection = styled(Paper)(({ theme }) => ({
  background:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.tertiary.main,
  borderRadius: '.5rem',
  border:
    theme.palette.mode === 'dark'
      ? `3px solid ${theme.palette.primary.main}`
      : `3px solid ${theme.palette.primary.dark}`,

  color:
    theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : theme.palette.primary.dark,

  boxShadow: colors.shadows.heavy,
  display: 'flex',
  flexDirection: 'column',
  animation: `${fadeIn} 0.5s ease-in-out`,
  padding: '0 0 1rem 0',

  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    padding: '0 0 1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    padding: '0 0 1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.large]: {
    padding: '0 0 1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    padding: '0 0 1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    padding: '0 0 1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    padding: '0 0 1rem 0',
  },

  '@media (min-width: 600px)': {
    padding: '0 0 .5rem 0',
  },
}));

const TodayButton = styled(Paper)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : theme.palette.tertiary.light,
  borderRadius: '16px',
  padding: '10px',

  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    padding: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    padding: '10px 10px 30px 10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.large]: {
    padding: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    padding: '10px',
  },

  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    padding: '1rem 0 .5rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    padding: '2rem 0 1rem 0',
  },

  border:
    theme.palette.mode === 'dark'
      ? `3px solid ${theme.palette.primary.main}`
      : `3px solid ${theme.palette.grey[100]}`,
  transition: 'all 0.3s ease',
  textAlign: 'center',
  transform: 'none',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.main
        : theme.palette.tertiary.dark,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[50]
        : theme.palette.grey[100],
    borderColor:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[50]
        : theme.palette.grey[100],
    boxShadow: colors.shadows.hover,
    '& .statNumber': {
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.secondary.light
          : theme.palette.grey[100],
      transform: 'scale(1.2)',
      transition: 'all 0.5s ease',
    },
    '& .statLabel': {
      color:
        theme.palette.mode === 'dark'
          ? theme.palette.grey[100]
          : theme.palette.grey[100],
      transform: 'scale(1.2)',
      transition: 'all 0.5s ease',
    },
  },
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontSize: '5rem',
  fontWeight: 800,
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.light
      : theme.palette.secondary.main,

  margin: 0,
  padding: '0 0',
  lineHeight: 1,
  display: 'block',

  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    fontSize: '4.5rem',
    padding: '0 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    fontSize: '5rem',
    padding: '0 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.large]: {
    fontSize: '6rem',
    padding: '0 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    fontSize: '6.5rem',
    padding: '0 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    fontSize: '6rem',
    padding: '0 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    fontSize: '10rem',
    padding: 0,
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[100]
      : theme.palette.grey[900],

  margin: 0,
  padding: '0.5rem 0',
  marginTop: '0.5rem',
  lineHeight: 1,

  [STICKY_MENU_MEDIA_QUERIES.tablet]: {
    fontSize: '2rem',
    padding: '0.5rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.desktop]: {
    fontSize: '2rem',
    padding: '0.75rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.large]: {
    fontSize: '2rem',
    padding: '0.75rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
    fontSize: '2rem',
    padding: '1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
    fontSize: '2rem',
    padding: '1rem 0',
  },

  [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
    fontSize: '2rem',
    padding: '1.25rem 0',
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2rem',
  animation: `${fadeIn} 0.5s ease-in-out`,
}));

const StatsGridContainer = styled(Box)({
  animation: `${fadeIn} 0.6s ease-out`,
});

// Shared spacing for action card rows. Vertical gap between rows mirrors
// horizontal gap within a row so mobile reads as one continuous stack.
const ACTION_ROW_GAP = { xs: 1, sm: 2, md: 2, lg: 2, xl: 1 } as const;

const HomePage: React.FC = () => {
  const theme = useTheme();
  const { nav, home } = useLocalisation();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    activeBookings: 0,
    checkInsToday: 0,
    checkOutsToday: 0,
    availableKennels: 0,
    overdueInvoices: 0,
  });

  const loadDashboardStats = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const stats = await bookingsAPI.getDashboardStats(today);
      setDashboardStats(stats);
    } catch (err: any) {
      const errorMessage =
        ERROR_MESSAGES.OPERATIONS.LOAD_FAILED('homepage content');
      setError(errorMessage);

      handleError(err, {
        context: 'loading dashboard stats',
        displayType: ErrorDisplayType.PAGE,
        showToast: false,
      });
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  // Combined into one 6-item grid so sm (2 cols) and md (3 cols)
  // both divide evenly with no orphaned/gapped row.
  const primaryActions: QuickAction[] = [
    {
      to: '/clients',
      icon: <People sx={{ fontSize: '4rem' }} />,
      titleKey: 'clients',
      description: '',
    },
    {
      to: '/pets',
      icon: <Pets sx={{ fontSize: '4rem' }} />,
      titleKey: 'pets',
      description: '',
    },
    {
      to: '/bookings',
      icon: <Event sx={{ fontSize: '4rem' }} />,
      titleKey: 'bookings',
      description: '',
    },
    {
      to: '/clients/new',
      icon: <PersonAdd sx={{ fontSize: '4rem' }} />,
      titleKey: 'newClient',
      description: '',
    },
    {
      to: '/quotes',
      icon: getCustomIcon('quotation', '4rem'),
      titleKey: 'quotes',
      description: '',
    },
    {
      to: '/quote/new',
      icon: getCustomIcon('quotationadd', '4rem'),
      titleKey: 'newQuote',
      description: '',
    },
  ];

  const bottomRowActions: QuickAction[] = [
    {
      to: '/kennels',
      icon: getCustomIcon('kennel', '4rem'),
      titleKey: 'kennels',
      description: '',
    },
    {
      to: '/calendar',
      icon: <CalendarMonth sx={{ fontSize: '4rem' }} />,
      titleKey: 'calendar',
      description: '',
    },
  ];

  const stats: Stat[] = [
    {
      number: dashboardStats.checkInsToday.toString(),
      labelKey: 'checkInsToday',
      tab: 'check-ins',
    },
    {
      number: dashboardStats.checkOutsToday.toString(),
      labelKey: 'checkOutsToday',
      tab: 'check-outs',
    },
    {
      number: dashboardStats.activeBookings.toString(),
      labelKey: 'activeBookings',
      tab: 'active-bookings',
    },
    {
      number: dashboardStats.overdueInvoices.toString(),
      labelKey: 'overdueInvoices',
      tab: 'overdue',
    },
  ];

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <VisuallyHiddenHeading title="Home" />
        <PageError message={error} onRetry={loadDashboardStats} />
      </Container>
    );
  }

  const handleTodayClick = () => {
    const today = new Date().toISOString().split('T')[0];
    navigate(`/today/check-ins?date=${today}`);
  };

  const handleStatClick = (
    tab: 'check-ins' | 'check-outs' | 'active-bookings' | 'overdue'
  ) => {
    if (tab === 'overdue') {
      navigate('/today/overdue');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    navigate(`/today/${tab}?date=${today}`);
  };

  const renderActionCard = (action: QuickAction) => (
    <Card
      key={action.to}
      component={Link}
      to={action.to}
      sx={{
        height: { xs: 'auto', sm: '100%' },
        minHeight: { xs: '80px', sm: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
        borderRadius: '0.25em',
        border: (theme) =>
          `2px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark}`,
        boxShadow: colors.shadows.heavy,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        transform: 'none',
        '&:hover': {
          transform: 'none !important',
          boxShadow: colors.shadows.hover,
          borderColor: (theme) => theme.palette.tertiary.light,
          background: colors.gradients.secondary,
          '& .action-icon': {
            transform: {
              xs: 'none',
              sm: 'scale(1.1)',
            },
            transition: 'all 0.3s ease',
          },
          '& .action-title': {
            transform: {
              xs: 'none',
              sm: 'scale(1.2)',
              letterSpacing: '5px',
            },
            transition: 'all 0.5s ease',
          },
        },
        '&:focus': {
          outline: `3px solid ${colors.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <ActionCardButton>
        <ActionIcon className="action-icon">{action.icon}</ActionIcon>
        <ActionTitle className="action-title" variant="h3">
          {nav(action.titleKey as any)}
        </ActionTitle>
      </ActionCardButton>
    </Card>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 5,
        pt: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },

        pb: { xs: 2, sm: 2, md: 1, lg: 0, xl: 0 },
      }}
    >
      <Box
        mb={{ xs: 2, sm: 2, md: 1, lg: 1, xl: 1 }}
        sx={{
          [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
            mb: '1rem',
          },
        }}
      >
        <VisuallyHiddenHeading title="Home" />
        <DateTimeDisplay />

        {/* All action card rows share gap so mobile reads as one stack. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: ACTION_ROW_GAP,
            [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
              gap: '1rem',
            },
          }}
        >
          <ActionCardsContainer
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: ACTION_ROW_GAP,
              [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
                gap: '1rem',
              },
            }}
          >
            {primaryActions.map(renderActionCard)}
          </ActionCardsContainer>

          <ActionCardsContainer
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(2, 1fr)',
              },
              gap: ACTION_ROW_GAP,
              [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
                gap: '1rem',
              },
            }}
          >
            {bottomRowActions.map(renderActionCard)}
          </ActionCardsContainer>
        </Box>
      </Box>

      {!loading ? (
        <StatsSection>
          <Typography
            variant="h2"
            onClick={handleTodayClick}
            sx={{
              cursor: 'pointer',
              color: (theme) => `${theme.palette.grey[100]}`,
              mb: 2,
              fontSize: '2rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              transition: 'all 0.1s ease',
              padding: '.8rem 0 1rem 0',
              [STICKY_MENU_MEDIA_QUERIES.tablet]: {
                padding: '.4rem 0 .5rem 2.5rem',
              },

              [STICKY_MENU_MEDIA_QUERIES.desktop]: {
                padding: '.4rem 0 .5rem 2.5rem',
              },
              [STICKY_MENU_MEDIA_QUERIES.large]: {
                padding: '.4rem 0 .5rem 2.5rem',
              },
              [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
                padding: '.5rem 0 .6rem 2.5rem',
              },
              [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
                padding: '.6rem 0 .7rem 4.5rem',
              },
              [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
                padding: '.8rem 0 1rem 2.5rem',
              },
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.dark
                  : theme.palette.primary.main,
              textAlign: { xs: 'center', sm: 'left' },
              borderBottom: (theme) =>
                theme.palette.mode === 'dark'
                  ? `3px solid ${theme.palette.primary.main}`
                  : `3px solid ${theme.palette.primary.dark}`,
              '&:hover': {
                letterSpacing: '.5rem',
                pl: '1.75rem',
                [STICKY_MENU_MEDIA_QUERIES.tablet]: {
                  pl: '1.75rem',
                },
                [STICKY_MENU_MEDIA_QUERIES.desktop]: {
                  pl: '1.75rem',
                },
                [STICKY_MENU_MEDIA_QUERIES.large]: {
                  pl: '1.75rem',
                },
                [STICKY_MENU_MEDIA_QUERIES.mediumLarge]: {
                  pl: '1.75rem',
                },
                [STICKY_MENU_MEDIA_QUERIES.xlarge]: {
                  pl: '3.75rem',
                },
                [STICKY_MENU_MEDIA_QUERIES.xxlarge]: {
                  pl: '1.75rem',
                },
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.tertiary.dark
                    : theme.palette.primary.dark,
              },
            }}
          >
            {home.overview.title}
          </Typography>

          <StatsGridContainer
            sx={{
              padding: '0 1rem',
              display: 'grid',
              mb: '.8rem',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 4,
            }}
          >
            {stats.map((stat, index) => (
              <TodayButton
                key={index}
                onClick={() => handleStatClick(stat.tab)}
              >
                <StatNumber className="statNumber" variant="inherit">
                  {stat.number}
                </StatNumber>
                <StatLabel className="statLabel" variant="body1">
                  {home.overview.stat(stat.labelKey as any)}
                </StatLabel>
              </TodayButton>
            ))}
          </StatsGridContainer>
        </StatsSection>
      ) : (
        <StatsSection>
          <LoadingContainer className="LOADINGCONTAINER">
            <LoadingSpinner
              compact
              spinnerSize={60}
              message=""
              color={{
                light: theme.palette.grey[100],
                dark: theme.palette.primary.light,
              }}
            />
          </LoadingContainer>
        </StatsSection>
      )}
    </Container>
  );
};

export default HomePage;
